import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { allowSkipQuestions, setNextGenEnabled } from 'ui_patient/AppSettings';
import Header, {
  defaultHeaderBackgroundColor,
  defaultHeaderLinkColor,
} from './Header';
import { useTranslation } from 'react-i18next';
import { mapFormValuesToRequestBody } from 'ui_patient/pages/MaskSelectorTool/MaskSelectorFormUtil';
import MaskSelectorForm, { steps, submitForm } from './MaskSelectorForm';
import moment from 'moment/moment';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { logStep } from 'ui_patient/pages/MaskSelectorTool/StepLogger';
import { buildNavMachine } from './PageNavigation';
import { interpret } from 'xstate';
import { Box } from '@mui/material';
import Footer from './Footer';
import { buildStepUrl } from './MaskSelectorTool';

import { getInitialValues } from 'ui_patient/pages/MaskSelectorTool/InitialValues';
import { uploadImages } from './utils/uploadImageUtil';

const maxDob = moment().subtract(13, 'years');
const usDateFormat = 'MM/DD/YYYY';
const auDateFormat = 'DD/MM/YYYY';

const HeaderContentFooter = ({
  tokenData,
  initialValues = {},
  customSubmitLogic,
  routePrefixPath,
  mugshotModel = null,
  clientId, // Add clientId prop
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { step } = useParams();
  const [state, setState] = useState('processing');
  const {
    token,
    mugshot_enabled,
    hme_org_code,
    hme_location_code,
    patient_code,
    skip_questions,
    nextgen_enabled,
    overrides,
  } = tokenData;

  setNextGenEnabled(nextgen_enabled);

  const handleBackLink = e => {
    // Prevent default so that "#" isn't appended to the URL which breaks the back link
    e.preventDefault();
    navigate(-1);
  };

  const [displayBackButton, setDisplayBackButton] = useState(false);
  const [displayHelpButton, setDisplayHelpButton] = useState(false);

  const [displayLogo, setDisplayLogo] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const [storedImages, setStoredImages] = useState({
    front: null,
    left: null,
    right: null,
    up: null,
  });

  const [headerBgColor, setHeaderBgColor] = useState(
    defaultHeaderBackgroundColor
  );
  const [headerLinkColor, setHeaderLinkColor] = useState(
    defaultHeaderLinkColor
  );

  const shouldDisplayBackButton = shouldDisplay => {
    setDisplayBackButton(shouldDisplay);
  };

  const shouldDisplayHelpButton = shouldDisplay => {
    setDisplayHelpButton(shouldDisplay);
  };

  const shouldDisplayLogo = shouldDisplay => {
    setDisplayLogo(shouldDisplay);
  };

  const shouldDisplayExpansion = shouldDisplay => {
    setExpanded(shouldDisplay);
  };

  const { t: tValidation } = useTranslation('validation_common');
  const { t: tComponents } = useTranslation('components.common');

  // Localization Strings
  const requiredValidationMessage = tValidation('requiredValidationMessage');

  const buildRegion = process.env.REACT_APP_BUILD_REGION;
  const dateFormat = buildRegion === 'AU' ? auDateFormat : tComponents('dateFormat');

  // Utility function to convert AU date format for API
  const convertDateForApi = (dateValue) => {
    if (buildRegion === 'AU' && dateValue) {
      const date = moment(dateValue, auDateFormat);
      return date.isValid() ? date.format(usDateFormat) : dateValue;
    }
    return dateValue;
  };


  const deployedSubmitLogic = async values => {
    const data = mapFormValuesToRequestBody({
      ...values,
      date_of_birth: convertDateForApi(values.date_of_birth)
    }, usDateFormat);

    try {
      await submitForm(data);
      setState('submitted');
      handleNext();
    } catch (e) {
      if (e.response !== undefined) {
        if (e.response.status === 502 || e.response.status === 503) {
          setState('try_again');
          navService.send('TRY_AGAIN');
        } else {
          setState('error');
          navService.send('ERROR');
        }
      } else {
        setState('error');
        navService.send('ERROR');
      }
    }
  };

  const handleNext = () => {
    navService.send('NEXT');
  };


  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(requiredValidationMessage),
    last_name: Yup.string().required(requiredValidationMessage),
    date_of_birth: Yup.string()
      .required('Date of birth is required')
      .test('is-valid-date', 'Please enter a valid date of birth', value => {
        if (!value) return false;
        const date = moment(value, dateFormat);
        return date.isValid();
      })
      .test('is-min-age', 'You must be at least 13 years old', value => {
        if (!value) return false;
        const date = moment(value, dateFormat);
        if (!date.isValid()) return false;
        return date.isBefore(maxDob);
      }),
    email: Yup.string().email().required(requiredValidationMessage),
    biometrics_consent_accepted: Yup.boolean().oneOf(
      [true],
      'You must accept the terms and conditions'
    ),
  });

  // Form Logic
  const formik = useFormik({
    validationSchema,
    initialValues: {
      token,
      hme_org_code,
      hme_location_code,
      patient_code,
      // ...completeInitialValues,
      biometrics_consent_accepted: false,
    },
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit:
      customSubmitLogic === undefined
        ? deployedSubmitLogic
        : async values => {
            customSubmitLogic(values);
            setState('submitted');
            handleNext();
          },
  });

  const {
    values,
    handleChange,
    errors,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    validateForm,
  } = formik;

  // The upgrade to React 18 caused this to surface in our code:
  // https://github.com/jaredpalmer/formik/issues/529
  //
  // We use formik.setFieldValue throughout our code to store questionnaire
  // responses and face measurement data.  This method call is async, which
  // means there is a potential lag between calling setFieldValue and when
  // formik would actually have that value.  What was happening was that
  // face measurement data wasn't being submitted at the end of the survey,
  // presumably due to this async lag and itt being the last thing collected.
  //
  // This solution is a bit of a hack, but seems to work.
  // It uses setTimeout() to queue a JS task, which means JS will only get to it
  // after it finishes with all current promises on its queue (which includes
  // any calls to setFieldValue)
  const handleSubmitProxy = () => {
    setTimeout(handleSubmit, 0);
  };


  const handleImageCapture = async ({ base64ImageUrl, direction }) => {
    try {
      console.log('Storing image...', direction);

      // Store the image locally with its direction
      setStoredImages(prevImages => ({
        ...prevImages,
        [direction]: base64ImageUrl,
      }));
    } catch (error) {
      console.error('Failed to store image:', error.message);
    }
  };

  const handleComplete = async measurementsData => {
    console.log('Scan is completed, uploading all images...', measurementsData);

    try {
      const images = Object.entries(storedImages)
        .filter(([_, image]) => image !== null)
        .map(([direction, image]) => ({
          direction,
          data: image,
        }));

      const uploadData = {
        images,
        center_id: clientId,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        dob: convertDateForApi(values.date_of_birth),
      };

      const response = await uploadImages(uploadData);
      console.log('Upload response:', response);

      // Clear stored images after successful upload
      setStoredImages({
        front: null,
        left: null,
        right: null,
        up: null,
      });
      handleSubmit();
    } catch (error) {
      console.error('Failed to upload images:', error);
    }
  };

  const termsComplete = values => values.biometrics_consent_accepted;

  const patientComplete = values => validationSchema.isValidSync(values);

  const getStep = (step, values, state) => {
    if (state === 'error') {
      return steps.error;
    }

    if (state === 'submitted') {
      return steps.complete;
    }

    let pageStep = step;

    if (step !== undefined && step.includes('?')) {
      pageStep = step.split('?')[0];
    }

    if (pageStep === undefined || pageStep === steps.start) {
      return steps.start;
    }

    if (
      pageStep === steps.patient ||
      !patientComplete(values) ||
      !termsComplete(values)
    ) {
      return steps.patient;
    }

    if (pageStep.startsWith(steps.sizing) || !state === 'submitted') {
      return pageStep;
    }

    // if (pageStep.startsWith('survey')) { //commented the survey part as it is not needed in phase 1
    //   return pageStep;
    // }

    if (pageStep.startsWith('hipaa_consent')) {
      return pageStep;
    }

    if (pageStep.startsWith('nextgen_scan_')) {
      return pageStep;
    }

    return steps.start;
  };

  const currentStep = getStep(step, values, state);

  //logStep(currentStep, token);

  const machineHead = buildNavMachine(
    currentStep,
    mugshot_enabled,
    allowSkipQuestions() ? skip_questions : false,
    overrides !== undefined ? overrides : []
  );

  const navService = interpret(machineHead);
  navService.start();

  // navService.subscribe(state => {
  //     console.log(`navService state: ${state.value}`);
  // });

  useEffect(() => {
    if (currentStep !== step) {
      let stepUrl = buildStepUrl(
        routePrefixPath,
        currentStep,
        token,
        mugshot_enabled,
        skip_questions,
        clientId // Add clientId here
      );

      navigate(stepUrl, { replace: true });
    }
  }, [
    navigate,
    currentStep,
    step,
    token,
    mugshot_enabled,
    routePrefixPath,
    skip_questions,
    clientId, // Add to dependency array
  ]);

  const shouldReplaceHistory = step => {
    // Help should always return to the page from which it was invoked.
    if (step.startsWith('sizing_scan_help')) {
      return false;
    }

    if (step.startsWith('nextgen_scan_help')) {
      return false;
    }

    // These are steps that we don't want to be able to go back to.
    // If the user navigates back we want to go to the step before them.
    const historyToReplace = [
      'sizing_scan_take_photo',
      'sizing_scan_retry',
      'nextgen_scan_scan_retry',
    ];

    let prevStepName = location.pathname.split('/')[2];

    return historyToReplace.findIndex(a => a === prevStepName) > -1;
  };

  const navigateToStep = (step) => {
    let stepUrl = buildStepUrl(
      routePrefixPath,
      step,
      token,
      mugshot_enabled,
      skip_questions,
      clientId // Add clientId here
    );

    if (shouldReplaceHistory(step)) {
      navigate(stepUrl, { replace: true });
    } else {
      navigate(stepUrl);
    }
  };

  const termsValid = termsComplete(values);

  const handleHelpLink = e => {
    // Prevent default so that "#" isn't appended to the URL which breaks the back link
    e.preventDefault();
    navService.send('HELP');
  };

  const handleDecline = () => {
    navService.send('DECLINE');
  };

  return (
    <Box sx={{ minHeight: '100svh' }}>
      {currentStep !== 'nextgen_scan_scan_interface' && (
        <Header
          displayBackButton={displayBackButton}
          displayHelpButton={displayHelpButton}
          handleBackLink={handleBackLink}
          handleHelpLink={handleHelpLink}
          displayLogo={displayLogo}
          expanded={expanded}
          background={headerBgColor}
          linkColor={headerLinkColor}
        />
      )}

      <Box flex={1} sx={{ bgcolor: 'white', overflow: 'visible' }}>
        <MaskSelectorForm
          handleBackLink={handleBackLink}
          handleDecline={handleDecline}
          currentStep={currentStep}
          setFieldValue={setFieldValue}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmitProxy}
          handleComplete={handleComplete}
          handleImageCapture={handleImageCapture}
          values={values}
          termsValid={termsValid}
          handleChange={handleChange}
          errors={errors}
          validateForm={validateForm}
          state={state}
          setState={setState}
          navService={navService}
          initialState={machineHead.initial}
          navigateToStep={navigateToStep}
          shouldDisplayBackButton={shouldDisplayBackButton}
          shouldDisplayHelpButton={shouldDisplayHelpButton}
          shouldDisplayLogo={shouldDisplayLogo}
          shouldDisplayExpansion={shouldDisplayExpansion}
          setHeaderBgColor={setHeaderBgColor}
          setHeaderLinkColor={setHeaderLinkColor}
          tokenData={tokenData}
          mugshotModel={mugshotModel}

        />
      </Box>
      <Footer />
    </Box>
  );
};
HeaderContentFooter.propTypes = {
  tokenData: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
  customSubmitLogic: PropTypes.func,
  routePrefixPath: PropTypes.string.isRequired,
  mugshotModel: PropTypes.object,
  clientId: PropTypes.string.isRequired, // Add clientId to PropTypes

};

export default HeaderContentFooter;
